<template>
  <div class="i-multiselect" ref="container">
    <!-- Trigger / Display -->
    <div
      ref="trigger"
      class="ms-trigger"
      :class="{
        'ms-open': isOpen,
        'ms-disabled': disabled,
        'ms-error': error
      }"
      @click="toggle"
    >
      <div class="ms-value-area">
        <!-- Chips dos selecionados -->
        <template v-if="selectedItems.length > 0">
          <span
            v-for="item in visibleChips"
            :key="item.value"
            class="ms-chip"
          >
            <span class="ms-chip-text">{{ item.label }}</span>
            <span class="ms-chip-remove" @click.stop="removeItem(item.value)">&times;</span>
          </span>
          <span v-if="hiddenCount > 0" class="ms-more">
            +{{ hiddenCount }}
          </span>
        </template>
        <!-- Placeholder -->
        <span v-else class="ms-placeholder">{{ placeholder }}</span>
      </div>
      <div class="ms-arrow">
        <svg
          class="ms-arrow-icon"
          :class="{ 'ms-arrow-open': isOpen }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </div>
    </div>

    <!-- Dropdown (teleported to body via fixed positioning) -->
    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="dropdown"
        class="ms-dropdown"
        :style="dropdownStyle"
        @click.stop
      >
        <!-- Filtro/Busca -->
        <div v-if="filter" class="ms-search-box">
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="ms-search-input"
            placeholder="Buscar..."
            @click.stop
          />
          <svg v-if="!search" class="ms-search-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
          </svg>
          <span v-else class="ms-search-clear" @click.stop="search = ''">&times;</span>
        </div>

        <!-- Lista de opções -->
        <div class="ms-options-list">
          <div
            v-for="option in filteredOptions"
            :key="option.value"
            class="ms-option"
            :class="{ 'ms-option-selected': isSelected(option.value) }"
            @click.stop="toggleOption(option.value)"
          >
            <div class="ms-checkbox" :class="{ 'ms-checkbox-checked': isSelected(option.value) }">
              <svg v-if="isSelected(option.value)" viewBox="0 0 20 20" fill="currentColor" class="ms-check-icon">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
            </div>
            <span class="ms-option-text">{{ option.label }}</span>
          </div>

          <!-- Sem resultados -->
          <div v-if="filteredOptions.length === 0" class="ms-empty">
            Nenhum resultado encontrado
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

export default {
  name: 'IMultiselect',
  props: {
    modelValue: {
      type: [Array, String],
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Selecione'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Boolean,
      default: true
    },
    maxSelectedLabels: {
      type: Number,
      default: 3
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const search = ref('');
    const container = ref(null);
    const trigger = ref(null);
    const dropdown = ref(null);
    const searchInput = ref(null);
    const dropdownStyle = ref({});

    const selectedValues = computed(() => {
      const val = props.modelValue;
      if (Array.isArray(val)) return val;
      if (typeof val === 'string' && val.length > 0) return val.split(',').map(v => v.trim());
      return [];
    });

    const selectedItems = computed(() => {
      return selectedValues.value
        .map(val => props.options.find(o => o.value === val))
        .filter(Boolean);
    });

    const visibleChips = computed(() => {
      return selectedItems.value.slice(0, props.maxSelectedLabels);
    });

    const hiddenCount = computed(() => {
      return Math.max(0, selectedItems.value.length - props.maxSelectedLabels);
    });

    const filteredOptions = computed(() => {
      if (!search.value) return props.options;
      const q = search.value.toLowerCase();
      return props.options.filter(o => o.label.toLowerCase().includes(q));
    });

    const isSelected = (value) => selectedValues.value.includes(value);

    const toggleOption = (value) => {
      const current = [...selectedValues.value];
      const idx = current.indexOf(value);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(value);
      }
      emit('update:modelValue', current);
      emit('change', current);
    };

    const removeItem = (value) => {
      const current = selectedValues.value.filter(v => v !== value);
      emit('update:modelValue', current);
      emit('change', current);
    };

    const updateDropdownPosition = () => {
      if (!trigger.value) return;
      const rect = trigger.value.getBoundingClientRect();
      const dropdownHeight = 300; // estimated max height
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < dropdownHeight && rect.top > spaceBelow;

      dropdownStyle.value = {
        position: 'fixed',
        left: rect.left + 'px',
        width: rect.width + 'px',
        zIndex: 9999,
        ...(openAbove
          ? { bottom: (window.innerHeight - rect.top + 4) + 'px' }
          : { top: (rect.bottom + 4) + 'px' }
        )
      };
    };

    const toggle = () => {
      if (props.disabled) return;
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        updateDropdownPosition();
        nextTick(() => {
          if (searchInput.value) searchInput.value.focus();
        });
      }
    };

    const close = () => {
      isOpen.value = false;
      search.value = '';
    };

    const onClickOutside = (e) => {
      if (!container.value) return;
      if (container.value.contains(e.target)) return;
      if (dropdown.value && dropdown.value.contains(e.target)) return;
      close();
    };

    const onScroll = () => {
      if (isOpen.value) updateDropdownPosition();
    };

    onMounted(() => {
      document.addEventListener('click', onClickOutside);
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onScroll);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', onClickOutside);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    });

    return {
      isOpen,
      search,
      container,
      trigger,
      dropdown,
      searchInput,
      dropdownStyle,
      selectedItems,
      visibleChips,
      hiddenCount,
      filteredOptions,
      isSelected,
      toggleOption,
      removeItem,
      toggle,
    };
  }
};
</script>

<style>
/* Dropdown (global - não scoped pois é teleportado ao body) */
.ms-dropdown {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1);
}

.ms-search-box {
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.ms-search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  background: #fff;
  color: #111827;
  box-sizing: border-box;
}

.ms-search-input:focus {
  border-color: #9ca3af;
}

.ms-search-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #9ca3af;
  pointer-events: none;
}

.ms-search-clear {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #9ca3af;
  font-size: 1.1rem;
  line-height: 1;
}

.ms-search-clear:hover {
  color: #6b7280;
}

.ms-options-list {
  max-height: 250px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.ms-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #111827;
  transition: background 0.1s;
}

.ms-option:hover {
  background: #f3f4f6;
}

.ms-option-selected {
  background: #eff6ff;
}

.ms-option-selected:hover {
  background: #dbeafe;
}

.ms-checkbox {
  width: 18px;
  height: 18px;
  min-width: 18px;
  border: 2px solid #d1d5db;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: all 0.15s;
}

.ms-checkbox-checked {
  background: var(--button-color, #556ee6);
  border-color: var(--button-color, #556ee6);
}

.ms-check-icon {
  width: 12px;
  height: 12px;
  color: #fff;
}

.ms-empty {
  padding: 0.75rem;
  color: #9ca3af;
  text-align: center;
  font-size: 0.875rem;
}
</style>

<style scoped>
.i-multiselect {
  position: relative;
  width: 100%;
}

/* Trigger */
.ms-trigger {
  display: flex;
  align-items: center;
  min-height: 54px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: border-color 0.15s;
}

.ms-trigger:hover {
  border-color: #9ca3af;
}

.ms-trigger.ms-open {
  border-color: #9ca3af;
  box-shadow: 0 0 0 1px #e5e7eb;
}

.ms-trigger.ms-disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

.ms-trigger.ms-error {
  border-color: #ef4444 !important;
}

/* Value area */
.ms-value-area {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 0.75rem;
  min-height: 38px;
  overflow: hidden;
}

.ms-placeholder {
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Chips */
.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--button-color, #556ee6);
  color: #fff;
  border-radius: 1rem;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  max-width: 200px;
}

.ms-chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ms-chip-remove {
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  opacity: 0.8;
  margin-left: 2px;
}

.ms-chip-remove:hover {
  opacity: 1;
}

.ms-more {
  font-size: 0.8rem;
  color: #6b7280;
  padding: 0.2rem 0.4rem;
  background: #f3f4f6;
  border-radius: 1rem;
}

/* Arrow */
.ms-arrow {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  color: #6b7280;
}

.ms-arrow-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.2s;
}

.ms-arrow-open {
  transform: rotate(180deg);
}
</style>
